import { useMemo, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { get } from 'lodash'
import { subMonths, addMonths, differenceInMonths, isBefore, isAfter } from 'date-fns'
import { deserializeDate, formatDateToBEFormatDateFns } from 'utils/date'
import { setCalendarWindowCenter } from 'slices/customer/customerDetailsSlice'

const WINDOW_HALF_MONTHS = 4
const EDGE_THRESHOLD_MONTHS = 1

export const useCalendarDateWindow = () => {
  const dispatch = useDispatch()
  const serializedWindowCenter = useSelector(state => get(state, 'CustomerDetails.routing.serializedWindowCenter'))
  const windowCenter = useMemo(
    () => (serializedWindowCenter ? deserializeDate(serializedWindowCenter) : new Date()),
    [serializedWindowCenter]
  )

  const { windowStartDate, windowEndDate } = useMemo(
    () => ({
      windowStartDate: formatDateToBEFormatDateFns(subMonths(windowCenter, WINDOW_HALF_MONTHS)),
      windowEndDate: formatDateToBEFormatDateFns(addMonths(windowCenter, WINDOW_HALF_MONTHS)),
    }),
    [windowCenter]
  )

  const expandWindowIfNeeded = useCallback(
    visibleDate => {
      const start = subMonths(windowCenter, WINDOW_HALF_MONTHS)
      const end = addMonths(windowCenter, WINDOW_HALF_MONTHS)
      const monthsFromStart = differenceInMonths(visibleDate, start)
      const monthsFromEnd = differenceInMonths(end, visibleDate)

      if (monthsFromStart < EDGE_THRESHOLD_MONTHS || monthsFromEnd < EDGE_THRESHOLD_MONTHS) {
        dispatch(setCalendarWindowCenter(visibleDate))
      }
    },
    [windowCenter, dispatch]
  )

  const ensureDateInWindow = useCallback(
    visibleDate => {
      const start = subMonths(windowCenter, WINDOW_HALF_MONTHS)
      const end = addMonths(windowCenter, WINDOW_HALF_MONTHS)
      if (isBefore(visibleDate, start) || isAfter(visibleDate, end)) {
        dispatch(setCalendarWindowCenter(visibleDate))
      }
    },
    [windowCenter, dispatch]
  )

  return { windowStartDate, windowEndDate, expandWindowIfNeeded, ensureDateInWindow }
}
